import { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, AlertCircle, ExternalLink, MapPin, Phone, Clock, ChevronRight } from 'lucide-react';
import { websiteApi } from '../../api';

export default function WebsitePublic() {
  const { subdomain } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const { data: resp } = await websiteApi.publicSite(subdomain);
      setData(resp);
    } catch (e) {
      setError(e.response?.data?.detail || 'Website not found');
    } finally {
      setLoading(false);
    }
  }, [subdomain]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (data?.content?.seo?.title) {
      document.title = data.content.seo.title;
    }
    if (data?.content?.seo?.description) {
      let meta = document.querySelector('meta[name="description"]');
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('name', 'description');
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', data.content.seo.description);
    }
  }, [data]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 size={26} className="animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-background">
        <div className="max-w-md text-center space-y-3">
          <AlertCircle size={40} className="text-red-500 mx-auto" />
          <h2 className="text-lg font-semibold">Website not found</h2>
          <p className="text-sm text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }

  const content = data?.content || {};
  const branding = data?.branding || { app_name: 'CredBloom', primary_color: null };
  const hero = content.hero || {};
  const about = content.about || {};
  const services = content.services || {};
  const gallery = content.gallery || [];
  const reviews = content.reviews || [];
  const contact = content.contact || {};
  const hours = content.hours || [];
  const ctaFooter = content.cta_footer || {};
  const businessName = content.business_name || '';
  const brandColor = branding.primary_color;

  const primaryStyle = brandColor ? { backgroundColor: brandColor, color: '#fff' } : {};

  const mapEmbedUrl = contact.lat && contact.lng
    ? `https://www.google.com/maps?q=${contact.lat},${contact.lng}&z=16&output=embed`
    : null;

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="website-public">
      {/* Sticky top bar */}
      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-md border-b border-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <p className="font-bold text-sm sm:text-base" style={{ fontFamily: 'Manrope' }}>{businessName}</p>
          {contact.phone && (
            <a href={`tel:${contact.phone}`} className="text-xs sm:text-sm text-primary hover:underline flex items-center gap-1.5" style={brandColor ? { color: brandColor } : undefined}>
              <Phone size={12} /> {contact.phone}
            </a>
          )}
        </div>
      </header>

      {/* Hero */}
      <section
        className="relative py-24 sm:py-36 px-6 text-center overflow-hidden"
        style={{
          background: brandColor
            ? `linear-gradient(135deg, ${brandColor}22, ${brandColor}05)`
            : 'linear-gradient(135deg, hsl(var(--primary) / 0.15), hsl(var(--primary) / 0.05))',
        }}
      >
        {gallery[0] && (
          <div
            className="absolute inset-0 bg-cover bg-center opacity-20"
            style={{ backgroundImage: `url(${gallery[0]})` }}
          />
        )}
        <div className="relative max-w-3xl mx-auto">
          <h1
            className="text-4xl sm:text-6xl font-bold leading-tight tracking-tight"
            style={{ fontFamily: 'Manrope' }}
            data-testid="site-hero-headline"
          >
            {hero.headline}
          </h1>
          {hero.subheadline && (
            <p className="text-base sm:text-xl text-muted-foreground mt-4 leading-relaxed">{hero.subheadline}</p>
          )}
          {hero.cta_label && contact.google_maps_uri && (
            <a
              href={contact.google_maps_uri}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 mt-8 px-8 py-3 rounded-full text-sm font-semibold text-white shadow-md hover:shadow-lg transition-shadow"
              style={brandColor ? { backgroundColor: brandColor } : { backgroundColor: 'hsl(var(--primary))' }}
              data-testid="site-hero-cta"
            >
              {hero.cta_label} <ExternalLink size={14} />
            </a>
          )}
          {content.rating && (
            <div className="mt-6 flex items-center justify-center gap-2 text-sm">
              <div className="flex items-center gap-0.5 text-amber-500">
                {Array.from({ length: 5 }).map((_, i) => (
                  <span key={i}>{i < Math.round(content.rating) ? '★' : '☆'}</span>
                ))}
              </div>
              <span className="text-muted-foreground">
                <strong className="text-foreground">{content.rating.toFixed(1)}</strong> ({content.review_count?.toLocaleString() || 0} reviews)
              </span>
            </div>
          )}
        </div>
      </section>

      {/* About */}
      {about.body && (
        <section className="py-16 sm:py-20 px-6" data-testid="site-about-section">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-bold mb-4" style={{ fontFamily: 'Manrope' }}>{about.title || 'About Us'}</h2>
            <div className="text-base text-muted-foreground whitespace-pre-line leading-relaxed">{about.body}</div>
          </div>
        </section>
      )}

      {/* Services */}
      {services.items?.length > 0 && (
        <section className="py-16 sm:py-20 px-6 bg-muted/30" data-testid="site-services-section">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-bold mb-8 text-center" style={{ fontFamily: 'Manrope' }}>{services.title || 'What We Offer'}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {services.items.map((s, i) => (
                <div key={i} className="p-5 rounded-xl border border-border bg-background hover:border-primary/50 transition-colors">
                  <div className="flex items-start gap-2">
                    <ChevronRight size={18} className="text-primary mt-0.5 shrink-0" style={brandColor ? { color: brandColor } : undefined} />
                    <div>
                      <p className="font-semibold">{s.name}</p>
                      {s.description && <p className="text-sm text-muted-foreground mt-1">{s.description}</p>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Gallery */}
      {gallery.length > 0 && (
        <section className="py-16 sm:py-20 px-6" data-testid="site-gallery-section">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-bold mb-8 text-center" style={{ fontFamily: 'Manrope' }}>Gallery</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {gallery.map((url, i) => (
                <img
                  key={i}
                  src={url}
                  alt=""
                  className="w-full h-48 sm:h-56 object-cover rounded-lg hover:scale-[1.02] transition-transform"
                  loading="lazy"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Reviews */}
      {reviews.length > 0 && (
        <section className="py-16 sm:py-20 px-6 bg-muted/30" data-testid="site-reviews-section">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-bold mb-8 text-center" style={{ fontFamily: 'Manrope' }}>What Guests Say</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {reviews.map((r, i) => (
                <div key={i} className="p-6 rounded-xl border border-border bg-background">
                  <div className="flex items-center gap-1 mb-2 text-amber-500">
                    {Array.from({ length: r.rating || 0 }).map((_, j) => <span key={j}>★</span>)}
                  </div>
                  <p className="text-sm italic leading-relaxed">&ldquo;{r.text}&rdquo;</p>
                  <p className="text-xs text-muted-foreground mt-3">— {r.author} · {r.time_description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Contact + Hours + Map */}
      <section className="py-16 sm:py-20 px-6" data-testid="site-contact-section">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold mb-8 text-center" style={{ fontFamily: 'Manrope' }}>Visit Us</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Map */}
            {mapEmbedUrl ? (
              <iframe
                src={mapEmbedUrl}
                title="Location Map"
                className="w-full h-72 lg:h-96 rounded-xl border border-border"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            ) : contact.address ? (
              <div className="w-full h-72 lg:h-96 rounded-xl border border-border bg-muted/50 flex items-center justify-center text-muted-foreground">
                <MapPin size={40} />
              </div>
            ) : null}

            <div className="space-y-6">
              {contact.address && (
                <div>
                  <p className="font-semibold flex items-center gap-2 mb-1"><MapPin size={14} className="text-primary" style={brandColor ? { color: brandColor } : undefined} /> Address</p>
                  <p className="text-sm text-muted-foreground">{contact.address}</p>
                  {contact.google_maps_uri && (
                    <a href={contact.google_maps_uri} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sm text-primary hover:underline mt-1.5" style={brandColor ? { color: brandColor } : undefined}>
                      Get directions <ExternalLink size={11} />
                    </a>
                  )}
                </div>
              )}

              {contact.phone && (
                <div>
                  <p className="font-semibold flex items-center gap-2 mb-1"><Phone size={14} className="text-primary" style={brandColor ? { color: brandColor } : undefined} /> Phone</p>
                  <a href={`tel:${contact.phone}`} className="text-sm text-primary hover:underline" style={brandColor ? { color: brandColor } : undefined}>{contact.phone}</a>
                </div>
              )}

              {hours.length > 0 && (
                <div>
                  <p className="font-semibold flex items-center gap-2 mb-2"><Clock size={14} className="text-primary" style={brandColor ? { color: brandColor } : undefined} /> Opening Hours</p>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    {hours.map((h, i) => <li key={i}>{h}</li>)}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* CTA footer */}
      {ctaFooter.headline && (
        <section
          className="py-16 sm:py-20 px-6 border-t border-border"
          style={{
            background: brandColor
              ? `linear-gradient(135deg, ${brandColor}18, ${brandColor}05)`
              : 'linear-gradient(135deg, hsl(var(--primary) / 0.10), hsl(var(--primary) / 0.02))',
          }}
        >
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-2xl sm:text-3xl font-bold mb-3" style={{ fontFamily: 'Manrope' }}>{ctaFooter.headline}</h2>
            {ctaFooter.subheadline && <p className="text-base text-muted-foreground mb-6">{ctaFooter.subheadline}</p>}
            {contact.google_maps_uri && (
              <a
                href={contact.google_maps_uri}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-8 py-3 rounded-full text-sm font-semibold text-white shadow-md hover:shadow-lg transition-shadow"
                style={brandColor ? { backgroundColor: brandColor } : { backgroundColor: 'hsl(var(--primary))' }}
                data-testid="site-cta-footer-btn"
              >
                {hero.cta_label || 'Visit Us'} <ExternalLink size={14} />
              </a>
            )}
          </div>
        </section>
      )}

      {/* Small footer with branding */}
      <footer className="py-6 px-6 border-t border-border text-center text-xs text-muted-foreground" data-testid="site-footer-branding">
        Website by {branding.app_name || 'CredBloom'}
      </footer>
    </div>
  );
}
